/**
 * BM25 scoring algorithm with field boosting
 * Industry-standard ranking used by Elasticsearch, Lucene, etc.
 */

import type { InvertedIndex, PostingEntry, IndexableField } from './index';
import { FIELD_BOOST, getDocumentFrequency } from './index';

// BM25 tuning parameters
const K1 = 1.2;  // Term frequency saturation (1.2-2.0 typical)
const B = 0.75;  // Document length normalization (0.75 typical)

/**
 * Calculate IDF (Inverse Document Frequency) for a term
 * Uses the BM25 variant: log((N - n + 0.5) / (n + 0.5) + 1)
 */
export function calculateIDF(totalDocs: number, docFreq: number): number {
  if (docFreq === 0) return 0;
  return Math.log((totalDocs - docFreq + 0.5) / (docFreq + 0.5) + 1);
}

/**
 * Calculate BM25 score for a single term in a document
 */
export function calculateTermScore(
  termFreq: number,
  docLength: number,
  avgDocLength: number,
  idf: number
): number {
  if (termFreq === 0) return 0;

  const numerator = termFreq * (K1 + 1);
  const denominator = termFreq + K1 * (1 - B + B * (docLength / avgDocLength));

  return idf * (numerator / denominator);
}

/**
 * Score a document for a single query term
 * Aggregates scores across all fields with boosting
 */
export function scoreDocumentForTerm(
  docId: string,
  postings: PostingEntry[],
  index: InvertedIndex,
  idf: number
): number {
  const docMeta = index.docs.get(docId);
  if (!docMeta) return 0;

  // Get all postings for this document
  const docPostings = postings.filter(p => p.docId === docId);
  if (docPostings.length === 0) return 0;

  let totalScore = 0;

  for (const posting of docPostings) {
    const fieldBoost = FIELD_BOOST[posting.field] || 1;
    
    // Use document's total token count for length normalization
    const termScore = calculateTermScore(
      posting.frequency,
      docMeta.tokenCount,
      index.avgDocLength,
      idf
    );

    totalScore += termScore * fieldBoost;
  }

  return totalScore;
}

/**
 * Score all matching documents for a list of query terms
 * Returns document IDs sorted by score (highest first)
 */
export function scoreDocuments(
  queryTerms: string[],
  index: InvertedIndex
): Array<{ docId: string; score: number }> {
  if (queryTerms.length === 0) return [];

  // Collect all document scores
  const scores = new Map<string, number>();

  for (const term of queryTerms) {
    const lowerTerm = term.toLowerCase();
    const postings = index.postings.get(lowerTerm);
    
    if (!postings || postings.length === 0) continue;

    const docFreq = getDocumentFrequency(index, lowerTerm);
    const idf = calculateIDF(index.totalDocs, docFreq);

    // Get unique document IDs from postings
    const docIds = new Set(postings.map(p => p.docId));

    for (const docId of docIds) {
      const termScore = scoreDocumentForTerm(docId, postings, index, idf);
      scores.set(docId, (scores.get(docId) || 0) + termScore);
    }
  }

  // Convert to array and sort by score
  const results = Array.from(scores.entries())
    .map(([docId, score]) => ({ docId, score }))
    .sort((a, b) => b.score - a.score);

  return results;
}

/**
 * Score documents with prefix matching support
 * Used for search-as-you-type functionality
 */
export function scoreDocumentsWithPrefix(
  queryTerms: string[],
  prefixTerms: string[],
  index: InvertedIndex
): Array<{ docId: string; score: number }> {
  if (queryTerms.length === 0 && prefixTerms.length === 0) return [];

  const scores = new Map<string, number>();

  // Score exact terms
  for (const term of queryTerms) {
    const lowerTerm = term.toLowerCase();
    const postings = index.postings.get(lowerTerm);
    
    if (!postings || postings.length === 0) continue;

    const docFreq = getDocumentFrequency(index, lowerTerm);
    const idf = calculateIDF(index.totalDocs, docFreq);

    const docIds = new Set(postings.map(p => p.docId));

    for (const docId of docIds) {
      const termScore = scoreDocumentForTerm(docId, postings, index, idf);
      scores.set(docId, (scores.get(docId) || 0) + termScore);
    }
  }

  // Score prefix terms (with slightly lower weight)
  const PREFIX_WEIGHT = 0.8;

  for (const prefix of prefixTerms) {
    const lowerPrefix = prefix.toLowerCase();

    // Find all tokens that start with this prefix
    for (const [token, postings] of index.postings) {
      if (!token.startsWith(lowerPrefix)) continue;

      const docFreq = getDocumentFrequency(index, token);
      const idf = calculateIDF(index.totalDocs, docFreq);

      const docIds = new Set(postings.map(p => p.docId));

      for (const docId of docIds) {
        const termScore = scoreDocumentForTerm(docId, postings, index, idf) * PREFIX_WEIGHT;
        scores.set(docId, (scores.get(docId) || 0) + termScore);
      }
    }
  }

  // Convert to array and sort by score
  const results = Array.from(scores.entries())
    .map(([docId, score]) => ({ docId, score }))
    .sort((a, b) => b.score - a.score);

  return results;
}

/**
 * Filter documents that must contain ALL terms (AND logic)
 * Returns only documents that have all required terms
 */
export function filterRequiredTerms(
  docScores: Array<{ docId: string; score: number }>,
  requiredTerms: string[],
  index: InvertedIndex
): Array<{ docId: string; score: number }> {
  if (requiredTerms.length === 0) return docScores;

  return docScores.filter(({ docId }) => {
    for (const term of requiredTerms) {
      const postings = index.postings.get(term.toLowerCase());
      if (!postings) return false;
      if (!postings.some(p => p.docId === docId)) return false;
    }
    return true;
  });
}

/**
 * Filter out documents containing excluded terms (NOT logic)
 */
export function filterExcludedTerms(
  docScores: Array<{ docId: string; score: number }>,
  excludedTerms: string[],
  index: InvertedIndex
): Array<{ docId: string; score: number }> {
  if (excludedTerms.length === 0) return docScores;

  return docScores.filter(({ docId }) => {
    for (const term of excludedTerms) {
      const postings = index.postings.get(term.toLowerCase());
      if (postings && postings.some(p => p.docId === docId)) {
        return false; // Exclude this document
      }
    }
    return true;
  });
}

